<template>
    <component
        :is="rootComponent"
        v-bind="rootProps"
        class="
            d-block
            w-100
            h-100
            position-relative
            no-underline-link
        "
    >

        <!-- Media -->
        <div
            class="
                d-flex
                flex-column
                w-100
                h-100
                overflow-hidden
            "
            :class="mediaPadClass"
        >
            <template
                v-if="modelValue.altImage && modelValue.image"
            >
                <FullImage
                    v-if="isMobile"
                    :image-object="modelValue.altImage"
                    :type="isMobile ? 'medium' : 'large'"
                    class="object-fit-cover w-100 h-100"
                    scalable
                />
                <FullImage
                    v-else
                    :image-object="modelValue.image"
                    :type="isMobile ? 'medium' : 'large'"
                    class="object-fit-cover w-100 h-100"
                    scalable
                />
            </template>
            <template
                v-else-if="modelValue.image"
            >
                <FullImage
                    :image-object="modelValue.image"
                    :type="isMobile ? 'medium' : 'large'"
                    class="object-fit-cover w-100 h-100"
                    scalable
                />
            </template>
            <template
                v-else-if="modelValue.videoSm && isMobile"
            >
                <BlockTypeVideoStatic
                    :model-value="modelValue.videoSm"
                    class="object-fit-cover w-100 h-100"
                />
            </template>
            <template
                v-else-if="modelValue.videoLg"
            >
                <BlockTypeVideoStatic
                    :model-value="modelValue.videoLg"
                    class="object-fit-cover w-100 h-100"
                />
            </template>
        </div>

        <!-- Text -->
        <div
            class="
                position-absolute
                top-0
                left-0
                w-100
                h-100
                d-flex
                flex-column
                pa-6
            "
            :class="textPositionClasses"
        >
            <div
                class="
                    fit-content
                    rounded-lg
                "
                :class="[
                    textAlignClass,
                    textThemeClass,
                    textPadClass,
                    textOverlayClass
                ]"
            >
                <div
                    v-if="modelValue.headingImage && !isHeadingImageError"
                    class="
                        pa-0
                        ma-0
                    "
                >
                    <v-img
                        :src="modelValue.headingImage.url"
                        :alt="modelValue.headingImage.title"
                        :aspect-ratio="modelValue.headingImage.aspectRatio"
                        width="auto"
                        :height="`calc(60px * ${modelValue.headingImage.scale})`"
                        class="mx-auto"
                        @error="onHeadingImageError"
                    />
                </div>
                <h2
                    v-else
                    class="
                        text-display-medium
                        text-md-display-large
                        font-weight-bold
                        text-uppercase
                        pa-0
                        ma-0
                    "
                    :class="[
                        textStrokeClass,
                        {
                            'text-drop-shadow': modelValue.headingDropShadow
                        }
                    ]"
                >
                    {{ modelValue.heading }}
                </h2>
                <div
                    v-if="modelValue.subHeading"
                    class="
                        mt-2
                        text-headline-small
                        text-md-headline-medium
                    "
                    :class="[
                        textStrokeClass,
                        {
                            'text-drop-shadow': modelValue.subHeadingDropShadow
                        }
                    ]"
                >
                    {{ modelValue.subHeading }}
                </div>
                <div
                    v-if="buttons.length"
                    class="
                        d-flex
                        flex-wrap
                        justify-center
                        mt-4
                    "
                >
                    <BtnUniversal
                        v-for="(button, index) in buttons"
                        :key="`hero-carousel-item-btn-${index}`"
                        :params="{ content: button.label, path: button.link }"
                        :is-nested-link="isBlockLink"
                        :theme="buttonTheme"
                        color="secondary"
                        :size="isMobile ? 'default' : 'large'"
                        class="ma-2"
                        :class="{
                            'bg-secondary': isBlockLink
                        }"
                        unset-default-label-case
                    >
                        {{ button.label }}
                    </BtnUniversal>
                </div>
            </div>
        </div>
    </component>
</template>

<script lang="ts" setup>
    import type {
        HeroCarouselItemZSplitV1
    } from '~/types';
    import {
        useContentBlocksUtils
    } from '~/composables';

    const props = defineProps<{
        modelValue: HeroCarouselItemZSplitV1['values'];
        isMobile: boolean;
        breakpoint: string;
        breakpointName: string;
    }>();

    const FullImage = defineAsyncComponent(() => import('~/components/media/FullImage/FullImage.vue'));
    const BlockTypeVideoStatic = defineAsyncComponent(() => import('~/components/content/blocks/v2/BlockTypeVideoStatic/BlockTypeVideoStatic.vue'));
    const BtnUniversal = defineAsyncComponent(() => import('~/components/BtnUniversal/BtnUniversal.vue'));

    const NuxtLink = resolveComponent('NuxtLink');

    const isMobile = computed(() => props.isMobile);
    const { getHeroCarouselItemBindings } = useContentBlocksUtils();

    const {
        buttons,
        isBlockLink,
        rootComponent,
        rootProps,
        textPositionClasses,
        textAlignClass,
        textThemeClass,
        textStrokeClass,
        buttonTheme,
        textOverlayClass,
        textPadClass,
        mediaPadClass
    } = getHeroCarouselItemBindings(() => props.modelValue, () => isMobile.value, NuxtLink);

    const isHeadingImageError = ref<boolean>(false);

    function onHeadingImageError(): void {
        isHeadingImageError.value = true;
    }
</script>

<style lang="scss"></style>
